import MapScale from '@/Enums/Map/MapScale.enum';
import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import GamReportType from '@/Enums/ReportingDashboard/Gam/ReportType.enum';
import GamReportRequest from '@/Enums/ReportingDashboard/ReportRequest/GamReportRequest.enum';
import type {
  IGamAdvertiserReport,
  IGamCampaignReport,
  IGamChannelReport,
  IGamCityReport,
  IGamCongressionalDistrictReport,
  IGamCountyReport,
  IGamCreativeReport,
  IGamDateReport,
  IGamDayOfWeekReport,
  IGamDaypartReport,
  IGamDealsReport,
  IGamDeviceReport,
  IGamDmaReport,
  IGamGeoFenceReport,
  IGamPlacementReport,
  IGamPublisherReport,
  IGamRegionReport,
  IGamTotalsReport,
  IGamZipReport,
} from '@/Interfaces/ReportingDashboard/Gam/Report.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IGamState } from '@/Interfaces/Slices/ReportingDashboard/Gam/State.interface';
import { GEO_BOUNDARIES, GEO_JOINT_BOUNDARIES, ZIPCODE_ENDPOINT } from '@/constants/endpoints';
import { borderIOHeaders } from '@/constants/geo';
import { defaultGamTotals } from '@/constants/reportingDashboard/gam';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { getBoundariesRequestBody } from '@/helpers/geo';
import { getEffectiveFilters } from '@/helpers/reportingDashboard/filters';
import { buildGamReportFilters, fetchGamCampaigns, gamReportRequestHandler } from '@/helpers/reportingDashboard/gam';
import type { RootState } from '@/store';
import type { GamGridView } from '@/types/ReportingDashboard/Gam.type';
import GamReportView from '@/types/ReportingDashboard/Gam.type';
import type { RequestMap } from '@/types/RequestMap.types';
import { AsyncThunk, AsyncThunkOptions, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

enum ActionPrefix {
  CampaignOptions = 'reportingDashboardGam/getCampaignOptions',
  Totals = 'reportingDashboardGam/getTotalsReport',
  TotalsReach = 'reportingDashboardGam/getTotalsReach',
  DateReport = 'reportingDashboardGam/getDateReport',
  DeviceReport = 'reportingDashboardGam/getDeviceReport',
  RegionReport = 'reportingDashboardGam/getRegionReport',
  ChannelReport = 'reportingDashboardGam/getChannelReport',
  StateBoundaries = 'reportingDashboardGam/getStateBoundaries',
  AdvertiserReport = 'reportingDashboardGam/getAdvertiserReport',
  CampaignReport = 'reportingDashboardGam/getCampaignReport',
  CreativeReport = 'reportingDashboardGam/getCreativeReport',
  PublisherReport = 'reportingDashboardGam/getPublisherReport',
  DaypartReport = 'reportingDashboardGam/getDaypartReport',
  DayOfWeekReport = 'reportingDashboardGam/getDayOfWeekReport',
  PlacementReport = 'reportingDashboardGam/getPlacementReport',
  DealsReport = 'reportingDashboardGam/getDealsReport',
  GeoFenceReport = 'reportingDashboardGam/getGeoFenceReport',
  DmaReport = 'reportingDashboardGam/getDmaReport',
  CountyReport = 'reportingDashboardGam/getCountyReport',
  CityReport = 'reportingDashboardGam/getCityReport',
  ZipReport = 'reportingDashboardGam/getZipReport',
  CongressionalDistrictReport = 'reportingDashboardGam/getCongressionalDistrictReport',
  DmaBoundaries = 'reportingDashboardGam/getDmaBoundaries',
  CountyBoundaries = 'reportingDashboardGam/getCountyBoundaries',
  CityBoundaries = 'reportingDashboardGam/getCityBoundaries',
  ZipBoundaries = 'reportingDashboardGam/getZipBoundaries',
  CongressionalDistrictBoundaries = 'reportingDashboardGam/getCongressionalDistrictBoundaries',
}

// Mirrors simplifiThunks.shouldFetchExplicitReport. The sentinel
// 'loading_data' means "initial state — fire". A real UUID means an
// in-flight request (skip). Null + non-empty data means fulfilled
// already (skip).
const shouldFetchExplicitReport = (report: unknown[] | undefined, requestId: string | null) => {
  if (requestId === DEFAULT_LOADING_REQUEST_ID) return true;
  return !report?.length && !requestId;
};

const getExplicitReportThunkOptions = (dataField: keyof IGamState): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = `${dataField}RequestId` as keyof IGamState;
    const {
      reportingDashboardGam: { [dataField]: data, [requestIdKey]: requestId, dataIsRefreshing },
    } = getState() as RootState;

    // Empty placeholder responses during on-demand snapshot warmup must not
    // re-trigger fetches — GamView's retry effect + shouldFetchExplicitReport
    // would otherwise loop on /gam/report indefinitely.
    if (dataIsRefreshing) return false;

    return shouldFetchExplicitReport(data as unknown[], requestId as string);
  },
});

export const getTotalsReport = createAsyncThunk<IGamTotalsReport, IAbortSignalThunkArg>(
  ActionPrefix.Totals,
  gamReportRequestHandler<IGamTotalsReport>(GamReportType.Total, { totals: defaultGamTotals }),
);

// Option B live Reach (Matt/Alex 2026-05-20): fires a fresh GAM ReportJob
// scoped to the user's current filter set and waits up to 120s for the
// platform-deduplicated value. Long-running by design; the Totals tile
// shows a "Calculating reach…" spinner over the Reach + Frequency cards
// while this resolves. v2 will swap to a faster path.
export interface IGamTotalsReachResponse {
  reach: number | null;
  frequency: number | null;
  dataIsRefreshing: boolean;
  timedOut?: boolean;
}
export const getGamTotalsReach = createAsyncThunk<IGamTotalsReachResponse, IAbortSignalThunkArg>(
  ActionPrefix.TotalsReach,
  async (payload, { getState, signal }) => {
    const state = getState() as RootState;
    const filters = getEffectiveFilters(state, ApiTabIndex.Gam);
    if (!filters.dateStart || !filters.dateEnd) {
      return { reach: null, frequency: null, dataIsRefreshing: false };
    }
    // Live reach takes the campaign selection as `orderIds` (it filters the GAM
    // query with ORDER_ID IN (...)); `campaignIds` is the snapshot-path name and
    // the validator drops it, which left Reach/Frequency network-wide next to a
    // campaign-scoped impression count (#4235).
    const { campaignIds, ...gamFilters } = buildGamReportFilters(state, filters.dateStart, filters.dateEnd);
    const body = {
      filters: { ...gamFilters, ...(campaignIds?.length ? { orderIds: campaignIds } : {}) },
    };
    // The endpoint is intentionally synchronous on the BE side — it polls
    // GAM until the ReportJob completes or hits the 120s deadline. Bump
    // axios timeout so the FE doesn't abort the request before the BE
    // returns its own timed-out response.
    const response = await axios.post('/reportingDashboard/gam/totalsReach', body, {
      signal: payload?.signal ?? signal,
      timeout: 150_000,
    });
    return response.data as IGamTotalsReachResponse;
  },
);

export const getDateReport = createAsyncThunk<IGamDateReport, IAbortSignalThunkArg>(
  ActionPrefix.DateReport,
  gamReportRequestHandler<IGamDateReport>(GamReportType.Date, { report: [] }),
  getExplicitReportThunkOptions('dateReport'),
);

export const getDeviceReport = createAsyncThunk<IGamDeviceReport, IAbortSignalThunkArg>(
  ActionPrefix.DeviceReport,
  gamReportRequestHandler<IGamDeviceReport>(GamReportType.Device, { report: [] }),
  getExplicitReportThunkOptions('deviceReport'),
);

export const getRegionReport = createAsyncThunk<IGamRegionReport, IAbortSignalThunkArg>(
  ActionPrefix.RegionReport,
  gamReportRequestHandler<IGamRegionReport>(GamReportType.Region, { report: [] }),
  getExplicitReportThunkOptions('regionReport'),
);

export const getChannelReport = createAsyncThunk<IGamChannelReport, IAbortSignalThunkArg>(
  ActionPrefix.ChannelReport,
  gamReportRequestHandler<IGamChannelReport>(GamReportType.Channel, { report: [] }),
  getExplicitReportThunkOptions('channelReport'),
);

// Per-scale boundary thunks. Read the matching breakdown from store at fire
// time, post geoIds to /geo/boundaries, return the FeatureCollection.
// Empty results return EMPTY_FC so MapSection renders the base map.
export const getGamStateBoundaries = createAsyncThunk<unknown, IAbortSignalThunkArg>(
  ActionPrefix.StateBoundaries,
  async (payload, { getState, signal }) => {
    const {
      reportingDashboardGam: { regionReport },
    } = getState() as RootState;
    const requestBody = getBoundariesRequestBody(MapScale.State, regionReport, 'region');
    if (!requestBody || !requestBody.geoIds?.length) {
      return { type: 'FeatureCollection', features: [] };
    }
    const response = await axios.post(GEO_BOUNDARIES, requestBody, { signal: payload?.signal ?? signal });
    return response.data;
  },
);

export const getAdvertiserReport = createAsyncThunk<IGamAdvertiserReport, IAbortSignalThunkArg>(
  ActionPrefix.AdvertiserReport,
  gamReportRequestHandler<IGamAdvertiserReport>(GamReportType.Advertiser, { report: [] }),
  getExplicitReportThunkOptions('advertiserReport'),
);

export const getCampaignReport = createAsyncThunk<IGamCampaignReport, IAbortSignalThunkArg>(
  ActionPrefix.CampaignReport,
  gamReportRequestHandler<IGamCampaignReport>(GamReportType.Campaign, { report: [] }),
  getExplicitReportThunkOptions('campaignReport'),
);

export const getCreativeReport = createAsyncThunk<IGamCreativeReport, IAbortSignalThunkArg>(
  ActionPrefix.CreativeReport,
  gamReportRequestHandler<IGamCreativeReport>(GamReportType.Creative, { report: [] }),
  getExplicitReportThunkOptions('creativeReport'),
);

export const getPublisherReport = createAsyncThunk<IGamPublisherReport, IAbortSignalThunkArg>(
  ActionPrefix.PublisherReport,
  gamReportRequestHandler<IGamPublisherReport>(GamReportType.Publisher, { report: [] }),
  getExplicitReportThunkOptions('publisherReport'),
);

export const getDaypartReport = createAsyncThunk<IGamDaypartReport, IAbortSignalThunkArg>(
  ActionPrefix.DaypartReport,
  gamReportRequestHandler<IGamDaypartReport>(GamReportType.Daypart, { report: [] }),
  getExplicitReportThunkOptions('daypartReport'),
);

export const getDayOfWeekReport = createAsyncThunk<IGamDayOfWeekReport, IAbortSignalThunkArg>(
  ActionPrefix.DayOfWeekReport,
  gamReportRequestHandler<IGamDayOfWeekReport>(GamReportType.DayOfWeek, { report: [] }),
  getExplicitReportThunkOptions('dayOfWeekReport'),
);

export const getPlacementReport = createAsyncThunk<IGamPlacementReport, IAbortSignalThunkArg>(
  ActionPrefix.PlacementReport,
  gamReportRequestHandler<IGamPlacementReport>(GamReportType.Placement, { report: [] }),
  getExplicitReportThunkOptions('placementReport'),
);

export const getDealsReport = createAsyncThunk<IGamDealsReport, IAbortSignalThunkArg>(
  ActionPrefix.DealsReport,
  gamReportRequestHandler<IGamDealsReport>(GamReportType.Deals, { report: [] }),
  getExplicitReportThunkOptions('dealsReport'),
);

export const getGeoFenceReport = createAsyncThunk<IGamGeoFenceReport, IAbortSignalThunkArg>(
  ActionPrefix.GeoFenceReport,
  gamReportRequestHandler<IGamGeoFenceReport>(GamReportType.GeoFence, { report: [] }),
  getExplicitReportThunkOptions('geoFenceReport'),
);

// Geo-scale breakdowns for the MapSection. Each thunk pulls one GAM
// ReportJob's snapshot keyed on the matching dim (METRO_NAME / COUNTY_NAME
// / CITY_NAME / POSTAL_CODE). Loaded on map-scale switch.
export const getDmaReport = createAsyncThunk<IGamDmaReport, IAbortSignalThunkArg>(
  ActionPrefix.DmaReport,
  gamReportRequestHandler<IGamDmaReport>(GamReportType.Dma, { report: [] }),
  getExplicitReportThunkOptions('dmaReport'),
);

export const getCountyReport = createAsyncThunk<IGamCountyReport, IAbortSignalThunkArg>(
  ActionPrefix.CountyReport,
  gamReportRequestHandler<IGamCountyReport>(GamReportType.County, { report: [] }),
  getExplicitReportThunkOptions('countyReport'),
);

export const getCityReport = createAsyncThunk<IGamCityReport, IAbortSignalThunkArg>(
  ActionPrefix.CityReport,
  gamReportRequestHandler<IGamCityReport>(GamReportType.City, { report: [] }),
  getExplicitReportThunkOptions('cityReport'),
);

export const getZipReport = createAsyncThunk<IGamZipReport, IAbortSignalThunkArg>(
  ActionPrefix.ZipReport,
  gamReportRequestHandler<IGamZipReport>(GamReportType.Zip, { report: [] }),
  getExplicitReportThunkOptions('zipReport'),
);

export const getCongressionalDistrictReport = createAsyncThunk<IGamCongressionalDistrictReport, IAbortSignalThunkArg>(
  ActionPrefix.CongressionalDistrictReport,
  gamReportRequestHandler<IGamCongressionalDistrictReport>(GamReportType.CongressionalDistrict, {
    report: [],
  }),
  getExplicitReportThunkOptions('congressionalDistrictReport'),
);

const EMPTY_FC = { type: 'FeatureCollection', features: [] };

export const getGamDmaBoundaries = createAsyncThunk<unknown, IAbortSignalThunkArg>(
  ActionPrefix.DmaBoundaries,
  async (payload, { getState, signal }) => {
    const {
      reportingDashboardGam: { dmaReport },
    } = getState() as RootState;
    // Geography table is keyed by the bare Nielsen 3-digit DMA code, which
    // matches what Simplifi sends ({geoIds: ["807","501","520",…]}). The
    // ribeye-api shaper has already stripped Google's "200" prefix off dmaId.
    const requestBody = getBoundariesRequestBody(MapScale.Dma, dmaReport, 'dmaId');
    if (!requestBody || !requestBody.geoIds?.length) return EMPTY_FC;
    const response = await axios.post(GEO_BOUNDARIES, requestBody, { signal: payload?.signal ?? signal });
    return response.data;
  },
);

export const getGamCountyBoundaries = createAsyncThunk<unknown, IAbortSignalThunkArg>(
  ActionPrefix.CountyBoundaries,
  async (payload, { getState, signal }) => {
    const {
      reportingDashboardGam: { countyReport },
    } = getState() as RootState;
    // Geography.geographic_id for counties is the 5-digit FIPS code (e.g.
    // "37063" for Durham, NC) — same format Simplifi sends. Use countyId,
    // not the human-readable `county` field.
    const requestBody = getBoundariesRequestBody(MapScale.County, countyReport, 'countyId', 250);
    if (!requestBody || !requestBody.geoIds?.length) return EMPTY_FC;
    const response = await axios.post(GEO_BOUNDARIES, requestBody, { signal: payload?.signal ?? signal });
    return response.data;
  },
);

// Congressional Districts use the same /geo/boundaries endpoint as State/
// DMA/County. The BE expects hyphenless codes ("NY10", not "NY-10") —
// the shaper has already stripped the hyphen for us.
export const getGamCongressionalDistrictBoundaries = createAsyncThunk<unknown, IAbortSignalThunkArg>(
  ActionPrefix.CongressionalDistrictBoundaries,
  async (payload, { getState, signal }) => {
    const {
      reportingDashboardGam: { congressionalDistrictReport },
    } = getState() as RootState;
    const requestBody = getBoundariesRequestBody(
      MapScale.CongressionalDistricts,
      congressionalDistrictReport,
      'districtId',
      250,
    );
    if (!requestBody || !requestBody.geoIds?.length) return EMPTY_FC;
    const response = await axios.post(GEO_BOUNDARIES, requestBody, { signal: payload?.signal ?? signal });
    return response.data;
  },
);

// City uses /geo/jointBoundaries — a different shape from the /geo/boundaries
// endpoint State/DMA/County hit. Mirrors Simplifi's getCityBoundaries. The
// response is `[{ type, featureCollection }]`; pluck the City entry.
export const getGamCityBoundaries = createAsyncThunk<unknown, IAbortSignalThunkArg>(
  ActionPrefix.CityBoundaries,
  async (payload, { getState, signal }) => {
    const {
      reportingDashboardGam: { cityReport },
    } = getState() as RootState;
    const cityIds = cityReport
      .map(r => r.cityId)
      .filter(id => id && id !== '0' && id !== '-1')
      .slice(0, 100);
    if (!cityIds.length) return EMPTY_FC;
    const response = await axios.post(
      GEO_JOINT_BOUNDARIES,
      { data: [{ geoType: MapScale.City, geoIds: cityIds }] },
      { signal: payload?.signal ?? signal },
    );
    return (
      (response.data as Array<{ type: string; featureCollection: unknown }> | undefined)?.find(
        ({ type }) => type === MapScale.City,
      )?.featureCollection ?? EMPTY_FC
    );
  },
);

// ZIP boundaries come from a third-party (vanitysoft) — different endpoint
// + method + headers than the other scales. Mirrors Simplifi's
// getZipBoundaries.
export const getGamZipBoundaries = createAsyncThunk<unknown, IAbortSignalThunkArg>(
  ActionPrefix.ZipBoundaries,
  async (payload, { getState, signal }) => {
    const {
      reportingDashboardGam: { zipReport },
    } = getState() as RootState;
    const zipCodes = zipReport
      .map(r => r.zip)
      .filter(z => /^\d{5}$/.test(z))
      .slice(0, 100);
    if (!zipCodes.length) return EMPTY_FC;
    const response = await axios.get(ZIPCODE_ENDPOINT, {
      params: { zipcode: zipCodes.join(',') },
      headers: borderIOHeaders,
      signal: payload?.signal ?? signal,
    });
    return response.data;
  },
);

// Per-view thunk dispatch map for the Impressions By Type grid radio toggle.
// Mirrors simplifiThunks.reportDispatch — `action` is the thunk to dispatch
// when the user selects that row, `reportRequest` is the cancellation key
// the request-saver attaches to the abort signal.
type GamActionSet = { action: AsyncThunk<any, any, any>; reportRequest: GamReportRequest };

export const gamReportDispatch: Record<GamGridView, GamActionSet> = {
  [GamReportView.Date]: { action: getDateReport, reportRequest: GamReportRequest.Date },
  [GamReportView.Channel]: { action: getChannelReport, reportRequest: GamReportRequest.Channel },
  [GamReportView.Device]: { action: getDeviceReport, reportRequest: GamReportRequest.Device },
  [GamReportView.Advertiser]: { action: getAdvertiserReport, reportRequest: GamReportRequest.Advertiser },
  [GamReportView.Campaign]: { action: getCampaignReport, reportRequest: GamReportRequest.Campaign },
  [GamReportView.Placement]: { action: getPlacementReport, reportRequest: GamReportRequest.Placement },
  [GamReportView.Creative]: { action: getCreativeReport, reportRequest: GamReportRequest.Creative },
  [GamReportView.Publisher]: { action: getPublisherReport, reportRequest: GamReportRequest.Publisher },
  [GamReportView.DayOfWeek]: { action: getDayOfWeekReport, reportRequest: GamReportRequest.DayOfWeek },
  [GamReportView.Daypart]: { action: getDaypartReport, reportRequest: GamReportRequest.Daypart },
  [GamReportView.Deals]: { action: getDealsReport, reportRequest: GamReportRequest.Deals },
  [GamReportView.GeoFence]: { action: getGeoFenceReport, reportRequest: GamReportRequest.GeoFence },
  // Geo submenu dispatch entries — picking a map scale fires the matching
  // breakdown thunk so the grid populates without needing the map switcher.
  [MapScale.State]: { action: getRegionReport, reportRequest: GamReportRequest.Region },
  [MapScale.Dma]: { action: getDmaReport, reportRequest: GamReportRequest.Dma },
  [MapScale.County]: { action: getCountyReport, reportRequest: GamReportRequest.County },
  [MapScale.Zip]: { action: getZipReport, reportRequest: GamReportRequest.Zip },
  [MapScale.CongressionalDistricts]: {
    action: getCongressionalDistrictReport,
    reportRequest: GamReportRequest.CongressionalDistrict,
  },
  [MapScale.City]: { action: getCityReport, reportRequest: GamReportRequest.City },
};

// Shared addCase loop fodder for the slice — mirrors simplifiThunks.reportMap.
export const reportMap: RequestMap<IGamState> = [
  ['dateReport', getDateReport],
  ['deviceReport', getDeviceReport],
  ['regionReport', getRegionReport],
  ['channelReport', getChannelReport],
  ['advertiserReport', getAdvertiserReport],
  ['campaignReport', getCampaignReport],
  ['creativeReport', getCreativeReport],
  ['publisherReport', getPublisherReport],
  ['daypartReport', getDaypartReport],
  ['dayOfWeekReport', getDayOfWeekReport],
  ['placementReport', getPlacementReport],
  ['dealsReport', getDealsReport],
  ['geoFenceReport', getGeoFenceReport],
  ['dmaReport', getDmaReport],
  ['countyReport', getCountyReport],
  ['cityReport', getCityReport],
  ['zipReport', getZipReport],
  ['congressionalDistrictReport', getCongressionalDistrictReport],
];

/**
 * Campaign selector options for the GAM filter bar. Keyed off the applied
 * whitelabel/advertiser scope and date range, so the list only offers orders
 * that actually delivered in the window (#4235).
 */
export const getGamCampaignOptions = createAsyncThunk<Array<{ label: string; value: number }>, IAbortSignalThunkArg>(
  ActionPrefix.CampaignOptions,
  async (payload, { getState, signal }) => {
    const state = getState() as RootState;
    const filters = getEffectiveFilters(state, ApiTabIndex.Gam);

    if (!filters.dateStart || !filters.dateEnd) {
      return [];
    }

    const { campaignIds: _ignored, ...scope } = buildGamReportFilters(state, filters.dateStart, filters.dateEnd);

    const { campaigns } = await fetchGamCampaigns({
      signal: payload?.signal ?? signal,
      filters: scope,
    });

    return campaigns.map(campaign => ({ label: campaign.name, value: campaign.id }));
  },
);
